export const SUPPORTED_LOCALES = ["en", "it", "fr", "es", "pt", "de", "zh", "nl"] as const;

export type LocaleId = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_HTML_LANG: Record<LocaleId, string> = {
  en: "en",
  it: "it",
  fr: "fr",
  es: "es",
  pt: "pt-PT",
  de: "de",
  zh: "zh-Hans",
  nl: "nl",
};

export const LOCALE_STORAGE_KEY = "dawn.locale";
export const DEFAULT_LOCALE: LocaleId = "en";

const SIMPLIFIED_CHINESE_TAGS = new Set(["zh-hans", "zh-cn", "zh-sg"]);
const TRADITIONAL_CHINESE_TAGS = new Set(["zh-hant", "zh-tw", "zh-hk", "zh-mo"]);

function normalizeLanguageTag(tag: string): string {
  return tag.toLowerCase().replace(/_/g, "-");
}

function isTraditionalChineseTag(tag: string): boolean {
  if (TRADITIONAL_CHINESE_TAGS.has(tag)) {
    return true;
  }
  return tag.startsWith("zh-hant") || tag.includes("-hant");
}

export function matchBrowserLanguageTags(tags: readonly string[]): LocaleId {
  for (const rawTag of tags) {
    const tag = normalizeLanguageTag(rawTag);
    if (!tag) {
      continue;
    }

    if (SIMPLIFIED_CHINESE_TAGS.has(tag)) {
      return "zh";
    }
    if (isTraditionalChineseTag(tag)) {
      continue;
    }

    const primary = tag.split("-")[0] ?? tag;
    if (primary === "zh") {
      return "zh";
    }

    if (isLocaleId(primary)) {
      return primary;
    }
  }

  return DEFAULT_LOCALE;
}

export function resolveBrowserLocale(): LocaleId {
  if (typeof navigator === "undefined") {
    return DEFAULT_LOCALE;
  }

  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  return matchBrowserLanguageTags(tags);
}

export function isLocaleId(value: string): value is LocaleId {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Returns stored locale, or detects from the browser, persists, and returns that on first visit. */
export function getStoredLocale(): LocaleId {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && isLocaleId(stored)) {
    return stored;
  }

  const detected = resolveBrowserLocale();
  setStoredLocale(detected);
  return detected;
}

export function setStoredLocale(locale: LocaleId): void {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}
